<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'

interface Props {
  open: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  closeOnBackdrop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  closeOnBackdrop: true,
})

const emit = defineEmits<{ close: [] }>()

const maxWidths = {
  sm:   '400px',
  md:   '560px',
  lg:   '760px',
  xl:   '1020px',
  full: '95vw',
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.open) emit('close')
}

onMounted(()  => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        style="
          position:fixed;inset:0;z-index:200;
          display:flex;align-items:center;justify-content:center;
          padding:16px;
        "
        @click.self="closeOnBackdrop && emit('close')"
      >
        <!-- Backdrop -->
        <div
          style="
            position:absolute;inset:0;
            background:rgba(0,0,0,0.65);
            backdrop-filter:blur(6px);
            -webkit-backdrop-filter:blur(6px);
          "
          @click="closeOnBackdrop && emit('close')"
        />

        <!-- Dialog -->
        <div
          :style="{
            position: 'relative',
            width: '100%',
            maxWidth: maxWidths[size],
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--shadow-pop)',
          }"
          role="dialog"
          aria-modal="true"
        >
          <!-- Header -->
          <div
            v-if="title || $slots.header"
            style="
              display:flex;align-items:center;justify-content:space-between;gap:8px;
              padding:16px 20px;
              border-bottom:1px solid var(--hairline);
            "
          >
            <h2
              v-if="title"
              style="font-size:15px;font-weight:600;color:var(--ink);margin:0;"
            >{{ title }}</h2>
            <slot name="header" />

            <!-- Close button -->
            <button
              style="
                display:flex;align-items:center;justify-content:center;
                margin-left:auto;
                width:28px;height:28px;border-radius:6px;border:none;
                background:transparent;color:var(--ink-muted);cursor:pointer;
                transition:background-color 0.15s,color 0.15s;flex-shrink:0;
              "
              title="Close"
              @click="emit('close')"
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div style="padding:20px;">
            <slot />
          </div>

          <!-- Footer -->
          <div
            v-if="$slots.footer"
            style="
              padding:14px 20px;
              border-top:1px solid var(--hairline);
              display:flex;align-items:center;justify-content:flex-end;gap:8px;
            "
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.18s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
.modal-enter-active > div:nth-child(2),
.modal-leave-active > div:nth-child(2) {
  transition: transform 0.18s ease, opacity 0.18s ease;
}
.modal-enter-from > div:nth-child(2),
.modal-leave-to > div:nth-child(2) {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}
</style>
